import { disposeMesh } from 'src/utils/object3DHelpers';

export enum AnimationAxis {
  X = 'x',
  Y = 'y',
  Z = 'z',
}

export type TEvent = 'loop' | 'finished';

class AnimationsManager {
  private readonly mixer: any = null;

  private action: any = null;

  private helperObjects: any[] = [];

  private mesh: any;

  constructor(
    private THREE: any,
    mesh: any,
  ) {
    this.mesh = mesh;
    this.mixer = new THREE.AnimationMixer(mesh);
  }

  /**
   * @param period is a period value in seconds.
   * @param offset is a percentage value. The current mesh scale is 100%.
   * */
  pulse(period = 4, offset = 10) {
    if (!this.mesh) throw new Error('Mesh is required');

    if (!this.mixer) return;

    const scale = this.mesh.scale || new this.THREE.Vector3();
    const offsetX = (scale.x * offset) / 100;
    const offsetY = (scale.y * offset) / 100;
    const offsetZ = (scale.z * offset) / 100;

    const frames = [
      scale.x,
      scale.y,
      scale.z, // time is 0
      scale.x + offsetX,
      scale.y + offsetY,
      scale.z + offsetZ, // time is period * 0.5
      scale.x,
      scale.y,
      scale.z, // time is period
    ];
    const times = [0, period * 0.5, period];

    const track = new this.THREE.VectorKeyframeTrack('.scale', times, frames);
    const clip = new this.THREE.AnimationClip(undefined, period, [track]);
    this.action = this.mixer.clipAction(clip, this.mesh);
  }

  outline() {
    if (!this.mesh) throw new Error('Mesh is required');

    if (!this.mixer) return;

    const glowMesh = this.mesh.clone();
    glowMesh.traverse((obj: any) => {
      if (!obj.isMesh) return;

      obj.material = new this.THREE.ShaderMaterial({
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: 5,
        uniforms: {
          u_offset: { value: 0.01 },
        },
        vertexShader:
          'uniform float u_offset;\n' +
          'void main() {\n' +
          '  vec3 pos = position + normal * u_offset;\n' +
          '  gl_Position = projectionMatrix * modelViewMatrix * vec4( pos, 1.0 );\n' +
          '}',
        fragmentShader:
          'varying mediump vec2 vUv;\n' +
          'void main(){\n' +
          '  gl_FragColor = vec4(1, 1, 1, 1);\n' +
          '}',
        side: this.THREE.BackSide,
      });
    });

    this.mesh.parent.add(glowMesh);
    // saving the mesh to be able tp clean it up later
    this.helperObjects.push(glowMesh);
  }

  /**
   * @param period is a period value in seconds.
   * @param offset is a percentage value. The current mesh scale is 100%.
   * */
  outlinePulse(period = 2, offset = 5) {
    if (!this.mesh) throw new Error('Mesh is required');

    if (!this.mixer) return;

    const glowMesh = this.mesh.clone();
    glowMesh.traverse((obj: any) => {
      if (!obj.isMesh) return;

      obj.material = new this.THREE.ShaderMaterial({
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: 5,
        uniforms: {
          u_offset: { value: 0.02 },
        },
        vertexShader:
          'uniform float u_offset;\n' +
          'void main() {\n' +
          '  vec3 pos = position + normal * u_offset;\n' +
          '  gl_Position = projectionMatrix * modelViewMatrix * vec4( pos, 1.0 );\n' +
          '}',
        fragmentShader:
          'varying mediump vec2 vUv;\n' +
          'void main(){\n' +
          '  gl_FragColor = vec4(1, 1, 1, 1);\n' +
          '}',
        side: this.THREE.DoubleSide,
      });
    });

    const initialOffset = 2;
    const glowMeshOffsetX = (glowMesh.scale.x * initialOffset) / 100;
    const glowMeshOffsetY = (glowMesh.scale.y * initialOffset) / 100;
    const glowMeshOffsetZ = (glowMesh.scale.z * initialOffset) / 100;
    glowMesh.scale.set(
      glowMesh.scale.x + glowMeshOffsetX,
      glowMesh.scale.y + glowMeshOffsetY,
      glowMesh.scale.z + glowMeshOffsetZ,
    );
    this.mesh.parent.add(glowMesh);
    // saving the mesh to be able tp clean it up later
    this.helperObjects.push(glowMesh);

    const scale = glowMesh.scale || new this.THREE.Vector3();
    const offsetX = (scale.x * offset) / 100;
    const offsetY = (scale.y * offset) / 100;
    const offsetZ = (scale.z * offset) / 100;

    const frames = [
      scale.x,
      scale.y,
      scale.z, // time is 0
      scale.x + offsetX,
      scale.y + offsetY,
      scale.z + offsetZ, // time is period * 0.5
      scale.x,
      scale.y,
      scale.z, // time is period
    ];
    const times = [0, period * 0.5, period];

    const track = new this.THREE.VectorKeyframeTrack('.scale', times, frames);
    const clip = new this.THREE.AnimationClip(undefined, period, [track]);
    this.action = this.mixer.clipAction(clip, glowMesh);
  }

  /**
   * @param period is a period value in seconds.
   * @param offset is a numeric value measured in meters.
   * */
  bounceUpDown(period = 4, offset = 0.2) {
    if (!this.mesh) throw new Error('Mesh is required');

    if (!this.mixer) return;

    const position = this.mesh.position || new this.THREE.Vector3();
    const frames = [
      position.x,
      position.y,
      position.z, // time is 0
      position.x,
      position.y + offset,
      position.z, // time is period * 0.5
      position.x,
      position.y,
      position.z, // time is period
    ];
    const times = [0, period * 0.5, period];

    const track = new this.THREE.VectorKeyframeTrack('.position', times, frames);
    const clip = new this.THREE.AnimationClip(undefined, period, [track]);
    this.action = this.mixer.clipAction(clip, this.mesh);
  }

  /**
   * @param period is a period value in seconds.
   * @param _axis is a string value, by default is AnimationAxis.Y.
   * */
  flip(period = 4, _axis: AnimationAxis = AnimationAxis.Y) {
    let axis;
    if (_axis === AnimationAxis.X) {
      axis = new this.THREE.Vector3(1, 0, 0);
    } else if (_axis === AnimationAxis.Z) {
      axis = new this.THREE.Vector3(0, 0, 1);
    } else {
      axis = new this.THREE.Vector3(0, 1, 0);
    }

    const frame = [
      new this.THREE.Quaternion().setFromAxisAngle(axis, this.mesh.rotation.y),
      new this.THREE.Quaternion().setFromAxisAngle(axis, this.mesh.rotation.y + Math.PI),
    ];
    const times = [0, period * 0.5, period];
    const track = new this.THREE.QuaternionKeyframeTrack(
      '.quaternion',
      times,
      [
        frame[0].x,
        frame[0].y,
        frame[0].z,
        frame[0].w,
        frame[1].x,
        frame[1].y,
        frame[1].z,
        frame[1].w,
        frame[0].x,
        frame[0].y,
        frame[0].z,
        frame[0].w,
      ],
      this.THREE.InterpolateSmooth,
    );

    const clip = new this.THREE.AnimationClip(null, period, [track]);
    this.action = this.mixer.clipAction(clip, this.mesh);
  }

  /**
   * @param period is a period value in seconds.
   * @param _axis is a string value, by default is AnimationAxis.Y.
   * */
  spin(period = 4, _axis: AnimationAxis = AnimationAxis.Y) {
    let axis;
    if (_axis === AnimationAxis.X) {
      axis = new this.THREE.Vector3(1, 0, 0);
    } else if (_axis === AnimationAxis.Z) {
      axis = new this.THREE.Vector3(0, 0, 1);
    } else {
      axis = new this.THREE.Vector3(0, 1, 0);
    }

    const frame0 = new this.THREE.Quaternion().setFromAxisAngle(axis, 0);
    const frame1 = new this.THREE.Quaternion().setFromAxisAngle(axis, Math.PI);
    const frame2 = new this.THREE.Quaternion().setFromAxisAngle(axis, Math.PI * 2);
    const track = new this.THREE.QuaternionKeyframeTrack(
      '.quaternion',
      [0, period * 0.5, period],
      [
        frame0.x,
        frame0.y,
        frame0.z,
        frame0.w,
        frame1.x,
        frame1.y,
        frame1.z,
        frame1.w,
        frame2.x,
        frame2.y,
        frame2.z,
        frame2.w,
      ],
    );

    const clip = new this.THREE.AnimationClip(null, period, [track]);
    this.action = this.mixer.clipAction(clip, this.mesh);
  }

  play() {
    if (!this.action) return;
    this.action.play();
  }

  stop() {
    if (this.action) {
      this.action.stop();
    }

    if (this.helperObjects) {
      this.helperObjects.forEach(disposeMesh);
    }
  }

  addEventListener(event: TEvent, cb: (e: any) => void) {
    this.mixer.addEventListener(event, cb);
  }

  removeEventListener(event: TEvent, cb: (e: any) => void) {
    this.mixer.removeEventListener(event, cb);
  }

  /**
   * Call it in onTick() method.
   * */
  update(delta: number) {
    if (this.mixer) {
      this.mixer.update(delta / 1000);
    }
  }
}

export default AnimationsManager;
